import React, {useMemo} from "react"
import {ProductSettingsRewardBusiness} from "@irati/types"
import {
  useClearProductSettingsRewardMutation,
  useProductSettingsQuery,
  useUpdateProductSettingsRewardMutation
} from "@irati/permissions/hooks"
import {match, P} from "ts-pattern"
import {Body, H4} from "@irati/ui/src/Typography"
import {Col, Row} from "@irati/ui/src/Layout"
import Input from "@irati/ui/src/FormItem/Input"
import Form from "@irati/ui/src/Form"
import validation from "@irati/utils/validation"
import {ValidationButton} from "@irati/ui/src/FormItem"
import Button from "@irati/ui/src/Button"
import ErrorText from "@irati/ui/src/ErrorText"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import {triggerSuccessToast} from "@irati/ui/src/ToastAlert/triggerToast"
import Loading from "@irati/ui/src/Loading"
import Spacing from "@irati/ui/src/Spacing"
import styled from "styled-components"
import adminTracking from "@irati-tracking/admin"
import Flex from "@irati/ui/src/Flex"
import useAuth from "@irati/auth/helpers/auth/useAuth"
import {Actions, State, useMinMaxReducer, useMinMaxReducerActions, useMinMaxReducerState} from "./reducer"

const ASSUMED_MIN = 100

export function RewardMinMaxSettings() {
  const {data, loading, error: err, load} = useProductSettingsQuery(useBusinessInfo())
  const store = useMinMaxReducer(useMemo(() => deriveInitialState(data?.rewardSettings?.business), [data]))
  const state = useMinMaxReducerState(store)
  const actions = useMinMaxReducerActions(store)

  if (loading || err) return <Loading load={load} />

  return match(state)
    .with({isEditing: true}, ({dataStatus, initialData, updates, error}) => {
      return (
        <Wrapper>
          <RewardMinMaxSettingsSet actions={actions} initialMin={initialData.min} min={updates?.min} max={updates?.max} />
          <Error error={error} />
          <ButtonWrapper>
            <SaveButton min={updates?.min} max={updates?.max} actions={actions} />
            <Button tertiary onClick={() => actions.cancelUpdate()}>
              <FormattedMessage id="platform.button.cancel" />
            </Button>
            {dataStatus === "set" && <ResetButton actions={actions} />}
          </ButtonWrapper>
        </Wrapper>
      )
    })
    .with({dataStatus: "default"}, () => {
      return (
        <Wrapper>
          <Button
            full
            onClick={() => {
              adminTracking.rewardMinMaxExploreLimits()
              actions.beginUpdate()
            }}>
            <FormattedMessage id="platform.button.set" />
          </Button>
        </Wrapper>
      )
    })
    .with({dataStatus: "set"}, ({initialData, error}) => {
      return (
        <Wrapper>
          <RewardMinMaxSettingsSet actions={actions} disabled initialMin={initialData.min} min={initialData.min} max={initialData.max} />
          <Error error={error} />
          <ButtonWrapper>
            <Button
              full
              onClick={() => {
                adminTracking.rewardMinMaxExploreLimits()
                actions.beginUpdate()
              }}>
              <FormattedMessage id="platform.button.set" />
            </Button>
            <ResetButton actions={actions} />
          </ButtonWrapper>
        </Wrapper>
      )
    })
    .exhaustive()
}

function Error({error}: {error?: string}) {
  const {formatMessage} = useIntl()
  if (!error) return null
  return (
    <Spacing marginBottom={2}>
      <ErrorText>{formatMessage({id: error})}</ErrorText>
    </Spacing>
  )
}

function ButtonWrapper({children}: {children: React.ReactNode}) {
  const count = React.Children.count(children)
  const [first, ...rest] = React.Children.toArray(children)
  if (count < 3) {
    return <Box gap={2}>{children}</Box>
  }
  return (
    <Box>
      <Box>{first}</Box>
      <Flex direction="row" justifyContent="center" gap={2}>
        {rest}
      </Flex>
    </Box>
  )
}

function RewardMinMaxSettingsSet({
  actions,
  initialMin,
  min,
  max,
  disabled
}: {
  actions: Actions
  initialMin: number
  min: number | undefined
  max: number | undefined
  disabled?: boolean
}) {
  return (
    <Row>
      <Col sm={12} lg={6}>
        <Input
          disabled={disabled}
          label="Min"
          name="min"
          type="number"
          value={min ? String(min / 100) : undefined}
          placeholder={String(initialMin / 100)}
          validation={validation
            .number("platform.defaultValidation.required")
            .positive("platform.validation.number.minOne")
            .integer("platform.validation.integersOnly")
            .min(1, "platform.validation.number.minOne")}
          onChange={(n) => {
            actions.setMin(n !== null ? Number(n) * 100 : undefined)
          }}
        />
      </Col>
      <Col sm={12} lg={6}>
        <Input
          disabled={disabled}
          label="Max"
          name="max"
          placeholder="unlimited"
          type="number"
          value={max ? String(max / 100) : undefined}
          validation={validation
            .number("platform.defaultValidation.required")
            .positive("platform.validation.number.minOne")
            .integer("platform.validation.integersOnly")
            .max(Number.MAX_SAFE_INTEGER - 1, "platform.validation.integersOnly")}
          onChange={(n) => {
            actions.setMax(n !== null ? Number(n) * 100 : undefined)
          }}
        />
      </Col>
    </Row>
  )
}

function Wrapper({children}: {children: React.ReactNode}) {
  return (
    <div>
      <H4 marginBottom={1}>
        <FormattedMessage id="admin.settings.rewards.minmax.heading" />
      </H4>
      <Form onSubmit={(e) => e.preventDefault()}>
        <Row>
          <Col sm={8}>
            <Spacing marginBottom={2}>
              <Body>
                <FormattedMessage id="admin.settings.rewards.minmax.body" />
              </Body>
            </Spacing>
            {children}
          </Col>
        </Row>
      </Form>
    </div>
  )
}

function SaveButton({min, max, actions}: {min: number | undefined; max: number | undefined; actions: Actions}) {
  const {businessId, teamId} = useBusinessInfo()
  const [updateProductSettingsReward, {load, error}] = useUpdateProductSettingsRewardMutation()
  const {formatMessage} = useIntl()

  return (
    <Box gap={2}>
      {error ? <Loading inline load={load} useLoader={false} /> : null}
      <ValidationButton
        full
        marginBottom={2}
        apolloState={load}
        type="submit"
        onClick={() => {
          if (!min || !max || min >= max) {
            actions.setError("admin.settings.rewards.minmax.validation.minMoreThanMax")
          } else {
            actions.setError(undefined)
            updateProductSettingsReward({variables: {businessId, teamId, data: {min, max}}}).then(() => {
              actions.cancelUpdate()
              triggerSuccessToast(formatMessage({id: "admin.settings.rewards.minmax.notification.success"}))
              adminTracking.rewardMinMaxUpdate({min, max})
            })
          }
        }}>
        <FormattedMessage id="platform.button.save" />
      </ValidationButton>
    </Box>
  )
}

function ResetButton({actions}: {actions: Actions}) {
  const {businessId, teamId} = useBusinessInfo()
  const {formatMessage} = useIntl()
  const [clearProductSettingsReward, {load, error}] = useClearProductSettingsRewardMutation({
    variables: {
      businessId,
      teamId
    },
    onCompleted: () => {
      actions.cancelUpdate()
      triggerSuccessToast(formatMessage({id: "admin.settings.rewards.minmax.notification.reset"}))
      adminTracking.rewardMinMaxReset()
    }
  })
  return (
    <Box gap={2}>
      {error ? <Loading inline load={load} useLoader={false} /> : null}
      <Box alignItems="center">
        <Button tertiary destructive apolloState={load} onClick={() => clearProductSettingsReward()}>
          <FormattedMessage id="platform.button.reset" />
        </Button>
      </Box>
    </Box>
  )
}

function deriveInitialState(data: ProductSettingsRewardBusiness | undefined): State {
  return match(data)
    .returnType<State>()
    .with(P.nullish, () => ({dataStatus: "default", initialData: {min: ASSUMED_MIN, max: undefined}}))
    .with({__typename: "ProductSettingsRewardDefault"}, ({defaultMin, defaultMax}) => ({
      dataStatus: "default",
      initialData: {
        min: defaultMin,
        max: defaultMax
      }
    }))
    .with({__typename: "ProductSettingsRewardSet"}, ({setMin, setMax}) => ({
      dataStatus: "set",
      initialData: {
        min: setMin,
        max: setMax
      }
    }))
    .exhaustive()
}

function useBusinessInfo() {
  const auth = useAuth()
  return {
    businessId: auth?.business?.uuid!,
    teamId: auth?.business?.rootTeamId!
  }
}

const Box = styled(Flex)`
  display: flex;
  flex-direction: column;
  justify-items: stretch;
  justify-content: center;
` as typeof Flex
